import React from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, BookOpen } from "lucide-react";
import { DocsViewer } from "@core/components/docs/DocsViewer";
import { ProductHeader } from "@core/components/layout/products/ProductHeader";
import { ProductSidebar } from "@core/components/layout/products/ProductSidebar";

/**
 * Page individuelle pour afficher un document markdown spécifique
 * Route: /docs/:docPath
 */
export default function DocsPage() {
  const { t } = useTranslation("forms");
  const { docPath } = useParams<{ docPath: string }>();

  if (!docPath) {
    return (
      <div className="flex flex-col min-h-screen-safe bg-[#030712] text-white">
        <ProductHeader productType="form" showAuth={true} />
        <div className="flex flex-1">
          <ProductSidebar productType="form" />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <BookOpen className="w-16 h-16 text-gray-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2">
                {t("guide.docpage.not_specified_title")}
              </h1>
              <p className="text-gray-400 mb-6">
                {t("guide.docpage.not_specified_desc")}
              </p>
              <Link
                to="/docs"
                className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {t("guide.docpage.back_to_docs")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen-safe bg-[#030712] text-white">
      <ProductHeader productType="form" showAuth={true} />
      <div className="flex flex-1">
        <ProductSidebar productType="form" />
        <div className="flex-1">
          {/* Header */}
          <div className="border-b border-gray-800 bg-white/[0.02]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Link
                    to="/docs"
                    className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors px-3 py-2 rounded-lg hover:bg-white/5"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    {t("guide.docpage.back")}
                  </Link>
                  <div className="h-6 w-px bg-gray-700"></div>
                  <div>
                    <h1 className="text-lg font-semibold text-white capitalize">
                      {docPath.replace(/-/g, " ").replace(".md", "")}
                    </h1>
                    <p className="text-sm text-gray-400">
                      {t("guide.docpage.subtitle")}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-400" />
                  <span className="text-sm text-gray-400">
                    {t("guide.docpage.document_label")}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <DocsViewer docPath={`${docPath}.md`} theme="blue" />
          </div>
        </div>
      </div>
    </div>
  );
}
